import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SupportButton from "./SupportButton";
import {
  LayoutDashboard,
  CandlestickChart,
  FolderOpen,
  Clock,
  Wallet,
  FileBarChart2,
  Settings,
  Search,
  Bell,
  HelpCircle,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Copy,
  Sun,
  Moon,
  Gift,
} from "lucide-react";
import { sidebarNav } from "../../dashboardMock";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";

const ICON_MAP = {
  layout: LayoutDashboard,
  candles: CandlestickChart,
  folder: FolderOpen,
  clock: Clock,
  wallet: Wallet,
  file: FileBarChart2,
  settings: Settings,
  copy: Copy,
  gift: Gift,
};

const NavItem = ({ item, active }) => {
  const Icon = ICON_MAP[item.icon] || LayoutDashboard;
  return (
    <Link
      to={`/dashboard/${item.key === "overview" ? "" : item.key}`}
      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] transition-all ${
        active
          ? "bg-[#1c3df5]/15 text-white border border-[#1c3df5]/30"
          : "text-white/60 hover:text-white hover:bg-white/[0.04] border border-transparent"
      }`}
    >
      <Icon size={17} strokeWidth={1.8} />
      <span>{item.label}</span>
    </Link>
  );
};

const DashboardLayout = ({ children, title, subtitle, activeKey = "overview" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const path = location.pathname.replace(/\/+$/, "");
  const current =
    path === "/dashboard" ? "overview" : path.split("/").pop();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userName = user?.name || "User";
  const userId = user?.id ? `BB-${user.id.slice(0, 8).toUpperCase()}` : "";
  const userInitials = userName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <div className={`min-h-screen bg-[#07080d] text-white flex ${theme === "light" ? "themed-light" : ""}`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[240px] shrink-0 flex-col border-r border-white/5 bg-[#08090f] sticky top-0 h-screen">
        <div className="px-5 h-16 flex items-center border-b border-white/5">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-white/95"></div>
            </div>
            <span className="font-display font-bold text-[17px]">Northwind</span>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-wider text-white/35 font-semibold">
            Menu
          </div>
          {sidebarNav.map((it) => (
            <NavItem key={it.key} item={it} active={current === it.key || (it.key === "overview" && current === "overview")} />
          ))}
        </nav>

        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center text-[13px] font-semibold">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-white truncate">{userName}</div>
              <div className="text-[11px] text-white/50 truncate">{userId}</div>
            </div>
            <button onClick={handleLogout} className="text-white/40 hover:text-white" title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <aside className="relative w-[280px] max-w-[82vw] bg-[#08090f] border-r border-white/5 flex flex-col animate-fadeIn">
            <div className="px-5 h-16 flex items-center justify-between border-b border-white/5">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/95"></div>
                </div>
                <span className="font-display font-bold text-[17px]">Northwind</span>
              </Link>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.05] flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
              <div className="px-3 mb-2 text-[10px] uppercase tracking-wider text-white/35 font-semibold">
                Menu
              </div>
              {sidebarNav.map((it) => (
                <NavItem key={it.key} item={it} active={current === it.key || (it.key === "overview" && current === "overview")} />
              ))}
            </nav>
            <div className="p-3 border-t border-white/5">
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center text-[13px] font-semibold">
                  {userInitials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-white truncate">{userName}</div>
                  <div className="text-[11px] text-white/50 truncate">{userId}</div>
                </div>
                <button onClick={handleLogout} className="text-white/40 hover:text-white">
                  <LogOut size={15} />
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col pb-16 lg:pb-0">
        {/* Topbar */}
        <header className="h-16 border-b border-white/5 bg-[#07080d]/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8 gap-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden w-9 h-9 inline-flex items-center justify-center rounded-lg border border-white/8 text-white/75 hover:text-white hover:bg-white/[0.05] shrink-0"
              aria-label="Menu"
            >
              <Menu size={17} />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] lg:text-[11px] uppercase tracking-wider text-white/40 truncate">{subtitle || "Dashboard"}</div>
              <div className="font-display font-semibold text-[16px] lg:text-[18px] text-white truncate">{title || "Overview"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden md:flex items-center gap-2 px-3 h-9 bg-white/[0.04] border border-white/8 rounded-lg w-[220px] xl:w-[260px]">
              <Search size={15} className="text-white/40" />
              <input
                placeholder="Search symbols"
                className="bg-transparent outline-none text-[13px] text-white placeholder:text-white/35 flex-1 min-w-0"
              />
            </div>
            <button className="hidden sm:inline-flex w-9 h-9 items-center justify-center rounded-lg border border-white/8 text-white/70 hover:text-white hover:bg-white/[0.05]">
              <HelpCircle size={15} />
            </button>
            <button
              onClick={toggle}
              data-testid="user-theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-white/8 text-white/70 hover:text-white hover:bg-white/[0.05]"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button className="relative w-9 h-9 inline-flex items-center justify-center rounded-lg border border-white/8 text-white/70 hover:text-white hover:bg-white/[0.05]">
              <Bell size={15} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#ef4444]"></span>
            </button>
            <button className="hidden xl:inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-white/8 text-white text-[13px] hover:bg-white/[0.05]">
              Live Account
              <ChevronDown size={13} />
            </button>
            <Link
              to="/dashboard/deposits"
              className="inline-flex items-center px-3 lg:px-4 h-9 bb-blue text-white text-[13px] font-medium rounded-lg"
            >
              Deposit
            </Link>
          </div>
        </header>

        <div className="p-4 lg:p-8">{children}</div>

        <SupportButton />

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#08090f]/95 backdrop-blur-md border-t border-white/8 grid grid-cols-5 h-16">
          {[
            sidebarNav[0], // Overview
            sidebarNav[1], // Trade
            sidebarNav[2], // Copy
            sidebarNav[3], // Affiliate
            sidebarNav[6], // Funds
          ].map((it) => {
            const Icon = ICON_MAP[it.icon] || LayoutDashboard;
            const isActive =
              current === it.key || (it.key === "overview" && current === "overview");
            return (
              <Link
                key={it.key}
                to={`/dashboard/${it.key === "overview" ? "" : it.key}`}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                  isActive ? "text-[#7d8eff]" : "text-white/55 hover:text-white"
                }`}
              >
                <Icon size={18} strokeWidth={1.8} />
                <span className="text-[10px] font-medium">{it.label}</span>
              </Link>
            );
          })}
        </nav>
      </main>
    </div>
  );
};

export default DashboardLayout;
